import { useState } from 'react';

import { useNavigate } from 'react-router-dom';

import PropTypes from 'prop-types';

import GateScreen from '#globalComponents/GateScreen/GateScreen';
import { useAuth } from '#globalContexts/AuthProvider';
import { useAddonTrial } from '#lib/addons/useAddonTrial';
import { BILLING_SETTINGS_PATH } from '#lib/constants/routes';
import { useStartAddon } from '#settingsFeature/models/billing/mutations';

import AddonBenefitList from './AddonBenefitList';
import AddonPurchaseConfirmModal from './AddonPurchaseConfirmModal';

/**
 * Generic add-on locked/upsell screen (shown via NotAllowedPage when a user without the
 * add-on's capability hits its routes). Branches on the trial/add-on state:
 *   - paying customer who never trialed → start the free trial here
 *   - trial used/expired                → activate as paid here
 *   - demo expired / cancelled          → upgrade / reactivate via settings
 * Always reassures that data is retained. Driven by an add-on config.
 */
const AddonUpsellPanel = ({ addon }) => {
	const navigate = useNavigate();
	const { orgPlan } = useAuth();
	const { canStartTrial, canActivate, hasTrial } = useAddonTrial(addon.key);
	const startAddon = useStartAddon();
	const [purchaseConfirmOpen, setPurchaseConfirmOpen] = useState(false);

	// Free trial and paid activation share the endpoint; the backend decides via the trialUsed latch.
	const activate = async () => {
		await startAddon.mutateAsync(addon.key);
		navigate(addon.route);
	};

	const handleAddon = async () => {
		if (!canStartTrial) {
			setPurchaseConfirmOpen(true);
			return;
		}
		try {
			await activate();
		} catch {
			// Global mutation error handling surfaces the toast.
		}
	};

	const goToSettings = () => navigate(BILLING_SETTINGS_PATH);
	const canBuyHere = canStartTrial || canActivate;
	// Demo and cancelled orgs that cannot buy here need a base plan first, so the add-on's offer would mislead.
	const showOffer = canBuyHere || (orgPlan !== 'demo' && orgPlan !== 'cancelled');

	let title;
	let description;
	let ctaLabel;

	if (canStartTrial) {
		title = `${addon.name} ${addon.trialDays} Tage kostenlos testen`;
		description = 'Alle Funktionen sofort verfügbar, ohne Risiko.';
		ctaLabel = `${addon.trialDays} Tage kostenlos testen`;
	} else if (canActivate) {
		title = `${addon.name}-Add-on aktivieren`;
		description = hasTrial
			? 'Ihre Testphase ist beendet. Ihre Daten bleiben erhalten.'
			: 'Schalten Sie alle Funktionen sofort frei.';
		ctaLabel = `${addon.name} aktivieren – ${addon.priceShort}`;
	} else if (orgPlan === 'demo') {
		title = `Ihre ${addon.name}-Testphase ist abgelaufen`;
		description =
			'Ihre Daten bleiben erhalten. Upgraden Sie auf einen kostenpflichtigen Plan, um weiter zu nutzen.';
		ctaLabel = 'Jetzt upgraden';
	} else if (orgPlan === 'cancelled') {
		title = 'Ihr Zugang ist beendet';
		description = 'Reaktivieren Sie Ihr Abonnement, um wieder zu nutzen. Ihre Daten bleiben erhalten.';
		ctaLabel = 'Abonnement reaktivieren';
	} else {
		title = 'Zugang erforderlich';
		description = `Aktivieren Sie das ${addon.name}-Add-on, um fortzufahren. Ihre Daten bleiben erhalten.`;
		ctaLabel = `${addon.name} aktivieren – ${addon.priceShort}`;
	}

	return (
		<GateScreen
			icon={addon.icon}
			title={title}
			action={{
				label: ctaLabel,
				onClick: canBuyHere ? handleAddon : goToSettings,
				loading: startAddon.isPending,
			}}
		>
			<p className="text-sm text-text">{description}</p>
			{showOffer && (
				<>
					<AddonBenefitList benefits={addon.benefits} />
					<p className="text-xs text-bgBase-800">
						{canStartTrial && 'Danach '}
						<span className="whitespace-nowrap font-semibold text-text">{addon.priceLabel}</span>, monatlich
						kündbar.
					</p>
				</>
			)}
			<AddonPurchaseConfirmModal
				addon={addon}
				isOpen={purchaseConfirmOpen}
				onClose={() => setPurchaseConfirmOpen(false)}
				onConfirm={activate}
				isLoading={startAddon.isPending}
			/>
		</GateScreen>
	);
};

AddonUpsellPanel.propTypes = {
	addon: PropTypes.shape({
		key: PropTypes.string.isRequired,
		name: PropTypes.string.isRequired,
		route: PropTypes.string.isRequired,
		priceLabel: PropTypes.string.isRequired,
		priceShort: PropTypes.string.isRequired,
		trialDays: PropTypes.number.isRequired,
		icon: PropTypes.elementType.isRequired,
		benefits: PropTypes.arrayOf(PropTypes.string).isRequired,
	}).isRequired,
};

export default AddonUpsellPanel;
